/**
 * Where a counterparty's request sits on the customer's own ladder.
 *
 * lp-012 §5. The three rungs are prose, so "is 110% past a floor of never below
 * 125%" was an inference the reader performed unaided and nothing checked —
 * which is why lp-010 §4's escalation, specified to fire "when a change is past
 * the concept's minimum", was never buildable.
 *
 * The split of work matters more than the arithmetic. The reader is asked for
 * ONE thing: what the change asks for, expressed in this concept's metric. The
 * comparison happens here, where it is deterministic, repeatable and can be
 * shown to the drafter as a sentence they can check. A reader that both
 * extracted and compared could be wrong twice and only one of those is
 * verifiable against anything.
 */

const DIRECTIONS = new Set(['higher_is_better', 'lower_is_better']);

function isNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Is `value` at least as good as `rung`, for the owner of the playbook?
 *
 * The direction is why this cannot be a bare comparison. Higher is better for
 * holdover rent and worse for a cure period, so `>=` is the right test for one
 * concept and exactly backwards for the next.
 */
function atLeastAsGood(value, rung, direction) {
  return direction === 'higher_is_better' ? value >= rung : value <= rung;
}

/**
 * Locate a request on a ladder, or return null.
 *
 * Null is the ordinary answer and must stay cheap: roughly half the vocabulary
 * has no natural metric, most changes carry no number, and a concept nobody
 * filled in has no rungs. Every one of those returns null and the card falls
 * back to the behaviour it had before this existed.
 */
export function locateOnLadder(requestedValue, ladder) {
  if (!isNumber(requestedValue)) return null;
  if (!ladder || typeof ladder !== 'object') return null;
  if (!DIRECTIONS.has(ladder.direction)) return null;
  if (!String(ladder.metric || '').trim()) return null;

  const { direction } = ladder;
  const stance = isNumber(ladder.stance) ? ladder.stance : null;
  const fallback = isNumber(ladder.fallback) ? ladder.fallback : null;
  const hardLimit = isNumber(ladder.hardLimit) ? ladder.hardLimit : null;
  if (stance === null && fallback === null && hardLimit === null) return null;

  // Worked from the floor up, because the floor is the rung that matters most
  // and the only one a partly-filled ladder can usually be trusted on. A
  // customer who states one number states the one they will not cross.
  if (hardLimit !== null && !atLeastAsGood(requestedValue, hardLimit, direction)) {
    return {
      placement: 'past_hard_limit',
      boundary: 'hardLimit',
      boundaryValue: hardLimit,
      metric: ladder.metric,
    };
  }

  if (fallback !== null && !atLeastAsGood(requestedValue, fallback, direction)) {
    return {
      placement: 'below_fallback',
      boundary: 'fallback',
      boundaryValue: fallback,
      metric: ladder.metric,
    };
  }

  if (stance !== null && !atLeastAsGood(requestedValue, stance, direction)) {
    return {
      placement: 'within_fallback',
      boundary: 'stance',
      boundaryValue: stance,
      metric: ladder.metric,
    };
  }

  return {
    placement: 'at_or_above_stance',
    boundary: 'stance',
    boundaryValue: stance,
    metric: ladder.metric,
  };
}

/**
 * The sentence the card shows, or null.
 *
 * Deliberately a statement of fact rather than a recommendation — the verdict
 * beside it is the recommendation, and a second one worded like advice would
 * read as two opinions that happen to agree. It names both numbers so a drafter
 * can check the claim without opening the playbook.
 */
export function ladderStatement(location, requestedValue) {
  if (!location || !isNumber(requestedValue)) return null;

  const metric = String(location.metric || '').trim();
  const asked = `${requestedValue} ${metric}`.trim();

  switch (location.placement) {
    case 'past_hard_limit':
      return `${asked} is past your limit of ${location.boundaryValue}.`;
    case 'below_fallback':
      return `${asked} is past your fallback of ${location.boundaryValue}, but within your limit.`;
    case 'within_fallback':
      return `${asked} is within your fallback, short of your position of ${location.boundaryValue}.`;
    case 'at_or_above_stance':
      return `${asked} is at or better than your position.`;
    default:
      return null;
  }
}
